import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Navigate, useSearchParams, useLocation, useNavigate } from "react-router";
import { useAuth } from "./AuthContext";
import { ssoExchangeUrl } from "../../utils/constants";
import PropTypes from "prop-types";
import { Spinner } from "react-bootstrap";
import { XCircleFill, CheckCircleFill } from "react-bootstrap-icons";

// Messages for the ?sso_error=<reason> boots_sso redirects to a protected
// route (e.g. BOOTS_SSO_POST_LOGIN_REDIRECT_URL).
const SSO_ERROR_MESSAGES = {
  not_provisioned:
    "Your account hasn't been set up for Boots SSO access yet. Please contact your administrator.",
  invalid_assertion:
    "We couldn't sign you in with Boots SSO. Please try again or contact your administrator.",
};

const STORE_SELECTION_PATH = "/store";

const EXEMPT_PATHS = [STORE_SELECTION_PATH, "/account", "/change-password"];
const VH_RESTRICTED_PATHS = [
  "/welcome",
  "/search",
  "/clinical-confirmation-steps",
  "/patient-record/clinical-details-not-found",
  "/patient-record/select-patient",
  "/patient-record/clinical-details",
];
const CLINICAL_RESTRICTED_PATHS = [
  "/confirmation-steps",
  "/patient-record/patient-details",
];

const matchesRestrictedPath = (pathname, restrictedList) =>
  restrictedList.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );

const PrivateRoute = ({ element: Element, ...rest }) => {
  const auth = useAuth();
  const { isAuthenticated, featureFlag, featureFlagLoading } = auth;
  const [searchParams, setSearchParams] = useSearchParams();
  const ssoCode = searchParams.get("sso_code");
  const ssoError = searchParams.get("sso_error");
  const [ssoFailed, setSsoFailed] = useState(false);
  const exchangedCodeRef = useRef(null);
  const handledSsoErrorRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const authenticated = JSON.parse(isAuthenticated);

  useEffect(() => {
    // exchangedCodeRef is the only guard against double-firing this POST -
    // deliberately no cleanup-based `cancelled` flag here. React's dev-mode
    // double effect invocation (mount -> cleanup -> mount) would set such a
    // flag on the harmless phantom cleanup, and then silently discard the
    // real response when it arrives after that, since the ref guard already
    // stops the second invocation from starting a redundant request.
    if (!ssoCode || exchangedCodeRef.current === ssoCode) {
      return;
    }
    exchangedCodeRef.current = ssoCode;

    axios
      .post(ssoExchangeUrl, { code: ssoCode })
      .then((response) => {
        const role = response.data.data?.role;
        if (role !== "healthcare-services" && role !== "clinical-services") {
          auth.logout();
          auth.showToast(
            "Error",
            "Your account does not have permission to access the Healthcare Professional portal.",
            "../assets/mhc.png",
            {
              icon: <XCircleFill />,
              className: "vh-toaster",
            },
          );
          navigate("/login", { replace: true });
          return;
        }
        auth.login(response.data.data);
        const isClinicalRole = response.data.data?.role === "clinical-services";
        localStorage.setItem("selectedService", isClinicalRole ? "clinical" : "healthCare");
        auth.showToast(
          "Success",
          "Logged in successfully!",
          undefined,
          {
            icon: <CheckCircleFill />,
            className: "vh-toaster",
          },
        );
      })
      .catch(() => {
        setSsoFailed(true);
        auth.logout();
        auth.showToast(
          "Error",
          SSO_ERROR_MESSAGES.not_provisioned,
          "../assets/mhc.png",
          {
            icon: <XCircleFill />,
            className: "vh-toaster",
          },
        );
        navigate("/login", { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoCode]);

  useEffect(() => {
    if (ssoCode && (JSON.parse(isAuthenticated) || ssoFailed)) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("sso_code");
      setSearchParams(nextParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoCode, isAuthenticated, ssoFailed]);

  useEffect(() => {
    if (!ssoError || handledSsoErrorRef.current === ssoError) {
      return;
    }
    handledSsoErrorRef.current = ssoError;

    const message = SSO_ERROR_MESSAGES[ssoError];
    if (message) {
      auth.showToast("Error", message, "../assets/mhc.png", { icon: <XCircleFill /> , className:"vh-toaster"});
    }

    auth.logout();
    navigate("/login", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoError]);

  if (ssoError || ssoFailed) {
    return <Navigate to="/login" replace />;
  }

  if (ssoCode && !ssoFailed && !JSON.parse(isAuthenticated)) {
    // Exchange is still in flight, or auth.login() hasn't flipped
    // isAuthenticated yet (it does so asynchronously) - hold off on
    // redirecting to /login until we know whether it actually succeeded.
    return null;
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  if (featureFlagLoading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant="info" className="my-custom-spinner" />
      </div>
    );
  }

  const hasSelectedStore = !!sessionStorage.getItem("selectedStore");
  const isExemptPath = EXEMPT_PATHS.includes(location.pathname);

  if (featureFlag && !hasSelectedStore && !isExemptPath) {
    return <Navigate to={STORE_SELECTION_PATH} replace />;
  }

  const isHealthCare = localStorage.getItem("selectedService") === "healthCare";

  const isRestrictedForCurrentService = isHealthCare
    ? matchesRestrictedPath(location.pathname, VH_RESTRICTED_PATHS)
    : matchesRestrictedPath(location.pathname, CLINICAL_RESTRICTED_PATHS);

  if (featureFlag && hasSelectedStore && isRestrictedForCurrentService) {
    return <Navigate to="/patient-record" replace />;
  }

  return <Element {...rest} />;
};

PrivateRoute.propTypes = {
  element: PropTypes.elementType.isRequired,
};

export default PrivateRoute;